import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormLabel,
  Input,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Spacer,
  Stack,
  StackDivider,
  useBoolean,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { HiOutlineFilter } from "react-icons/hi";
import { useLocation } from "react-router-dom";

type FilterFormValues = {
  createdFrom: string;
  createdTo: string;
  dealerType: string[];
};

interface Props {
  onSubmit(searchParams: URLSearchParams): void;
}

export const DealersFilterPopover = ({ onSubmit }: Props) => {
  const [isEditing, setIsEditing] = useBoolean();
  const location = useLocation();
  const sp = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const { control, handleSubmit } = useForm<FilterFormValues>({
    defaultValues: {
      createdFrom: sp.get("createdFrom") || "",
      createdTo: sp.get("createdTo") || "",
      dealerType: sp.get("dealerType")?.split(",") || [],
    },
  });

  const parseParameters = useCallback(
    (data: FilterFormValues) => {
      const newSp = new URLSearchParams(location.search);

      for (const [key, value] of Object.entries(data)) {
        const hasValue = value && (!Array.isArray(value) || value.length);

        if (hasValue) {
          newSp.set(key, value as string);
        } else {
          newSp.delete(key);
        }
      }

      onSubmit(newSp);
      setIsEditing.off();
    },
    [location.search]
  );

  return (
    <Popover
      isOpen={isEditing}
      onOpen={setIsEditing.on}
      onClose={setIsEditing.off}
      closeOnBlur={false}
      isLazy
      lazyBehavior="keepMounted"
    >
      <PopoverTrigger>
        <Button leftIcon={<HiOutlineFilter />}>Filters</Button>
      </PopoverTrigger>

      <PopoverContent display="flex">
        <PopoverHeader>Filters</PopoverHeader>
        <PopoverCloseButton />
        <PopoverBody>
          <form onSubmit={handleSubmit(parseParameters)}>
            <VStack
              align="start"
              gap={6}
              divider={<StackDivider borderColor="gray.200" />}
            >
              <Box width="100%">
                <Controller
                  name="createdFrom"
                  control={control}
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel htmlFor="createdFrom">
                        Date Added (GMT)
                      </FormLabel>
                      <FormLabel fontSize="12" htmlFor="createdFrom">
                        From
                      </FormLabel>
                      <Input {...field} id="createdFrom" type="date" />
                    </FormControl>
                  )}
                />
                <Spacer mt={2} />
                <Controller
                  name="createdTo"
                  control={control}
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel fontSize="12" htmlFor="createdTo">
                        To
                      </FormLabel>
                      <Input {...field} id="createdTo" type="date" />
                    </FormControl>
                  )}
                />
              </Box>

              <Box width="100%">
                <Controller
                  name="dealerType"
                  control={control}
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel htmlFor="dealerType">Dealer Type</FormLabel>
                      <CheckboxGroup {...field} isDisabled={field.disabled}>
                        <Stack
                          spacing={[1, 5]}
                          direction={["column", "row"]}
                          id="dealerType"
                        >
                          <Checkbox value="Internal">Internal</Checkbox>
                          <Checkbox value="Billable">Billable</Checkbox>
                        </Stack>
                      </CheckboxGroup>
                    </FormControl>
                  )}
                />
              </Box>

              <Box width="100%">
                <ButtonGroup display="flex" justifyContent="flex-end">
                  <Button type="button" onClick={setIsEditing.off}>
                    Cancel
                  </Button>
                  <Button type="submit" colorScheme="brand.blue">
                    Apply Filters
                  </Button>
                </ButtonGroup>
              </Box>
            </VStack>
          </form>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
